import {coordsToKey} from "../coords/coordsToKey";
import {isHexTokenIsBlockerForMovement} from "../movement/isHexTokenIsBlockerForMovement";
import {getUnitSpeed} from "../../configs/units";
import {ADDITIONAL_SUPPLY_SPEED} from "../../configs/supply";
import {HEX_DIRECTIONS} from "../../constants/geometry";
import {isPlayerPolicy} from "../factions/isPlayerPolicy";
import {POLICIES} from "../../constants/policies";
import {checkIfUnitTypeIsFlyingForFaction} from "../../configs/factions";
import {calculateFlyingMovementCells} from "../movement/calculateFlyingMovementCells";

/**
 * @param {Object} hexGridRenderer
 * @param {Object} hexes
 * @param {Array} units
 * @param {Object} selectedUnit
 * @param {Array} players
 * @param {Array<string>} noStopHexKeys - ячейки, на которых нельзя ЗАКОНЧИТЬ
 *   перемещение, но сквозь которые можно пройти (ячейки отрядов, погибших на
 *   этом этапе от ловушек Гоблинов: погибший отряд показан на них до конца
 *   этапа, см. selectGoblinsTrapDeathHexKeys)
 */
export const calculateAvailableMovementCells = (hexGridRenderer, hexes, units, selectedUnit, players, noStopHexKeys = []) => {
  const noStopHexKeysSet = new Set(noStopHexKeys);
  const {
    hexKey,
    ownerId,
  } = selectedUnit;

  const owner = players.find(p => p.id === ownerId);
  const isEcoanarchism = isPlayerPolicy(owner, POLICIES.ECOANARCHISM);

  const baseSpeed = getUnitSpeed(selectedUnit.type);
  
  // Проверяем, есть ли у отряда дополнительное снабжение
  const hasAdditionalSupply = selectedUnit.additionalSupply === true;
  
  // Если есть дополнительное снабжение, увеличиваем дальность перемещения на 1
  const speed = hasAdditionalSupply ? baseSpeed + ADDITIONAL_SUPPLY_SPEED : baseSpeed;

  // Летающий отряд (по паре тип + фракция ОТРЯДА — соблазненный летун летает и
  // у нового владельца) не зависит от промежуточных ячеек пути, см.
  // calculateFlyingMovementCells
  if (checkIfUnitTypeIsFlyingForFaction(selectedUnit.type, selectedUnit.factionId)) {
    return calculateFlyingMovementCells(
      hexGridRenderer, hexes, units, selectedUnit, baseSpeed, speed, noStopHexKeysSet
    );
  }

  const reachableCells = new Set();
  const additionalSupplyCells = new Set(); // Ячейки, достижимые только за счет дополнительного снабжения
  const paths = {}; // Хранит путь для каждой ячейки (массив координат от старта до ячейки)
  const visited = new Set(); // Для предотвращения зацикливания
  const [startQ, startR] = hexKey.split(',').map(Number);

  // Инициализируем путь для стартовой позиции
  paths[`${startQ},${startR}`] = [{q: startQ, r: startR}];

  // Рекурсивная функция DFS
  const dfs = (currentHexes, currentDepth) => {
    const newReachableCells = new Set();

    // Для каждой ячейки на текущей глубине
    for (const [q, r] of currentHexes) {
      // Для каждого направления
      for (const [dq, dr] of HEX_DIRECTIONS) {
        const newQ = q + dq;
        const newR = r + dr;
        const newKey = coordsToKey(newQ, newR);

        // Пропускаем если уже посещали
        if (visited.has(newKey)) {
          continue;
        }

        // Проверяем, что ячейка существует на поле
        if (!hexGridRenderer.hexes[newKey]) {
          continue;
        }

        // Проверяем содержимое ячейки
        const hexToken = hexes[newKey];
        const unitOnCell = units.find(u => u.hexKey === newKey);

        // если на ячейке вражеский отряд или непроходимый токен (гора/вода), то он заблокирован для перемещения
        const isBlockerForMovement = (!!unitOnCell && unitOnCell.ownerId !== ownerId)
          || isHexTokenIsBlockerForMovement(hexToken);

        if (isBlockerForMovement) {
          // Не добавляем в доступные и не идем дальше по рекурсии
          newReachableCells.add({newQ, newR, canStopHere: false, canGoThrough: false});
        } else if (unitOnCell && hexToken) {
          // Свой отряд на непустой ячейке - не добавляем в доступные, но продолжаем рекурсию
          newReachableCells.add({newQ, newR, canStopHere: false, canGoThrough: true});
        } else if (unitOnCell && !hexToken) {
          // Свой отряд на пустой ячейке - не добавляем в доступные, а сквозь него
          // проходим на тех же условиях, что и сквозь саму пустую ячейку: только
          // при Экоанархизме. Свой отряд проходимости ячейки не меняет
          newReachableCells.add({newQ, newR, canStopHere: false, canGoThrough: isEcoanarchism});
        } else if (!unitOnCell && hexToken) {
          // Ячейка с проходимым жетоном и без отряда - добавляем в доступные и продолжаем рекурсию
          newReachableCells.add({newQ, newR, canStopHere: !noStopHexKeysSet.has(newKey), canGoThrough: true});
        } else {
          // Ячейка и без жетона, и без отряда - добавляем в доступные, а насквозь
          // проходим только при Экоанархизме
          newReachableCells.add({
            newQ, newR, canStopHere: !noStopHexKeysSet.has(newKey), canGoThrough: isEcoanarchism,
          });
        }
      }
    }

    const nextHexes = [];
    const nextDepth = currentDepth + 1;

    newReachableCells.forEach(item => {
      const {newQ, newR, canStopHere, canGoThrough} = item
      const hexKey = coordsToKey(newQ, newR)

      visited.add(hexKey)

      // Для каждой ячейки находим её родителя - откуда мы пришли
      // parentHex - это координаты ячейки, из которой мы пришли в текущую
      let parentKey = null;
      for (const [pq, pr] of currentHexes) {
        // Проверяем, является ли текущая [newQ, newR] соседом [pq, pr]
        for (const [dq, dr] of HEX_DIRECTIONS) {
          if (pq + dq === newQ && pr + dr === newR) {
            parentKey = coordsToKey(pq, pr);
            break;
          }
        }
        if (parentKey) break;
      }

      // Сохраняем путь до этой ячейки
      if (parentKey && paths[parentKey]) {
        paths[hexKey] = [...paths[parentKey], {q: newQ, r: newR}];
      }

      if (canStopHere) {
        // console.log('canStopHere', hexKey)
        reachableCells.add(hexKey);
        
        // Если это ячейка на расстоянии больше базовой скорости, но в пределах расширенной,
        // и у отряда есть дополнительное снабжение, помечаем её
        if (hasAdditionalSupply && currentDepth > baseSpeed) {
          additionalSupplyCells.add(hexKey);
        }
      }

      if (canGoThrough) {
        // console.log('canGoThrough', hexKey)
        nextHexes.push([newQ, newR]);
      }

      // console.log('go:', currentDepth, 'from', q, r, 'to', newQ, newR);
      // console.log('stop: speed limit')
    })

    if (nextDepth <= speed) {
      // console.log('DFS', nextDepth);
      dfs(nextHexes, nextDepth);
    }
  };

  // Запускаем DFS с начальной позиции
  visited.add(`${startQ},${startR}`)
  dfs([[startQ, startR]], 1);

  return {
    cells: [...reachableCells],
    additionalSupplyCells: [...additionalSupplyCells],
    paths
  };
}
